import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import AttendanceIssueModal from "../../components/Modals/personalinfos/attendanceIssue";
import { useLocation, useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   RESPONSIVE HOOK
───────────────────────────────────────────── */
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
    width,
  };
}

/* ─────────────────────────────────────────────
   ICONS
───────────────────────────────────────────── */
const IconBriefcase = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <path d="M2 12h20" />
  </svg>
);
const IconHome = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const IconPlane = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2L16 11l3.5-3.5A2.12 2.12 0 0 0 16.5 4.5L13 8 4.8 6.2a1 1 0 0 0-.9.3L3 7.5l7 4.7-1.5 1.5-3-.75L4 14l2.5 1.5L8 18l1.5-1.5L9 14l4.7 7 1-.9a1 1 0 0 0 .3-.9z" />
  </svg>
);
const IconPalmtree = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h11z" />
    <path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-9z" />
    <path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35z" />
    <path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14z" />
  </svg>
);
const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconArrow = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─────────────────────────────────────────────
   FINANCIAL YEAR
───────────────────────────────────────────── */
function getCurrentFY() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 4) return { start: `${year}-04-01`, end: `${year + 1}-03-31` };
  return { start: `${year - 1}-04-01`, end: `${year}-03-31` };
}
const { start: FY_START, end: FY_END } = getCurrentFY();

/* ─────────────────────────────────────────────
   DATE / TIME HELPERS
───────────────────────────────────────────── */
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isWeekend(dateStr) {
  const day = new Date(dateStr + "T00:00:00").getDay();
  return day === 0 || day === 6;
}
function timeToMinutes(timeStr) {
  if (!timeStr) return null;

  const match = timeStr.trim().match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)$/i
  );

  if (!match) return null;

  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === "PM" && hour !== 12) {
    hour += 12;
  }

  if (period === "AM" && hour === 12) {
    hour = 0;
  }

  return hour * 60 + minute;
}
function formatTime(timeStr) {
  if (!timeStr) return null;

  // Match: 10:48:45am, 10:48:45 AM, 2:08:45pm
  const match = timeStr.trim().match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)$/i
  );

  if (!match) {
    return timeStr;
  }

  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const period = match[3].toUpperCase();

  return `${hour}:${minute} ${period}`;
}

/* ─────────────────────────────────────────────
   SESSION GROUPING
───────────────────────────────────────────── */
function groupIntoSessions(segments) {
  if (!segments.length) return [];
  const sorted = [...segments].sort(
    (a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0)
  );
  const sessions = [];
  let current = null;
  for (const seg of sorted) {
    if (!current || current.source !== seg.source) {
      if (current) sessions.push(current);
      current = {
        source: seg.source,
        firstLogin: seg.time,
        lastLogout: seg.logoutTime,
        count: 1,
      };
    } else {
      current.count++;
      if (seg.logoutTime) {
  if (
    !current.lastLogout ||
    timeToMinutes(seg.logoutTime) > timeToMinutes(current.lastLogout)
  ) {
    current.lastLogout = seg.logoutTime;
  }
}
    }
  }
  if (current) sessions.push(current);
  return sessions;
}

/* ─────────────────────────────────────────────
   ISSUE DETECTION
───────────────────────────────────────────── */
const LATE_LOGIN_CUTOFF = 10 * 60;
const EARLY_LEAVE_CUTOFF = 18 * 60 + 45;

function detectIssues(day) {
  const todayStr = toDateStr(new Date());
  const issues = [];
  if (day.date >= todayStr) return issues;
  if (["weekend", "holiday", "leave", "travel"].includes(day.status)) return issues;
  if (day.segments.length === 0) {
    issues.push("no_login");
    return issues;
  }
  const sorted = [...day.segments].sort(
    (a, b) => (timeToMinutes(a.time) ?? 0) - (timeToMinutes(b.time) ?? 0)
  );
  const firstLoginMin = timeToMinutes(sorted[0].time);
  const lastLogout = sorted.reduce((latest, s) => {
    if (!s.logoutTime) return latest;

    if (
        !latest ||
        timeToMinutes(s.logoutTime) > timeToMinutes(latest)
    ) {
        return s.logoutTime;
    }

    return latest;
}, null);
  const lastLogoutMin = timeToMinutes(lastLogout);
  const hasNoLogout = sorted.some((s) => !s.logoutTime);
  if (firstLoginMin > LATE_LOGIN_CUTOFF) issues.push("late_login");
  if (hasNoLogout) issues.push("no_logout");
  if (lastLogoutMin !== null && lastLogoutMin < EARLY_LEAVE_CUTOFF)
    issues.push("early_logout");
  return issues;
}

/* ─────────────────────────────────────────────
   PERIOD CONFIG
───────────────────────────────────────────── */
const PERIOD_LABEL = {
  week: "This week",
  month: "This month",
  year: "This financial year",
};
const FILTER_LABEL = {
  office: "office logins",
  wfh: "work-from-home logins",
  travel: "travel days",
  leave: "leave days",
};

function getPeriodRange(period) {
  const today = new Date();
  const todayStr = toDateStr(today);
  const fyEnd = todayStr < FY_END ? todayStr : FY_END;
  if (period === "week") {
    const mon = new Date(today);
    mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const start = toDateStr(mon) < FY_START ? FY_START : toDateStr(mon);
    return [start, fyEnd];
  }
  if (period === "month") {
    const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
    return [start < FY_START ? FY_START : start, fyEnd];
  }
  return [FY_START, fyEnd];
}

/* ─────────────────────────────────────────────
   BUILD DAYS
───────────────────────────────────────────── */
function buildDaysForPeriod(empRecord, period) {
  const { holidays = [], leaves = [], travels = [], logins = [] } = empRecord;
  const holidayMap = new Map(holidays.map((h) => [h.date, h.name]));
  const leaveMap = new Map();
  for (const l of leaves) {
    let d = new Date(l.start + "T00:00:00");
    const end = new Date(l.end + "T00:00:00");
    while (d <= end) {
      leaveMap.set(toDateStr(d), l.type);
      d.setDate(d.getDate() + 1);
    }
  }
  const travelSet = new Set();
  for (const t of travels) {
    let d = new Date(t.start + "T00:00:00");
    const end = new Date(t.end + "T00:00:00");
    while (d <= end) {
      travelSet.add(toDateStr(d));
      d.setDate(d.getDate() + 1);
    }
  }
  const loginMap = new Map();
  for (const l of logins) {
    const dateKey = l.date;
    if (!loginMap.has(dateKey)) loginMap.set(dateKey, []);
    loginMap.get(dateKey).push({
      time: l.loginTime || l.time,
      logoutTime: l.logoutTime || null,
      source: l.source,
    });
  }
  const [startStr, endStr] = getPeriodRange(period);
  const result = [];
  let d = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  while (d <= end) {
    const dateStr = toDateStr(d);
    const weekend = isWeekend(dateStr);
    const holName = holidayMap.get(dateStr);
    const leaveType = leaveMap.get(dateStr);
    const isTravel = travelSet.has(dateStr);
    const segments = loginMap.get(dateStr) || [];
    let status = "workday";
    if (weekend) status = "weekend";
    else if (holName) status = "holiday";
    else if (leaveType) status = "leave";
    else if (isTravel) status = "travel";
    result.push({ date: dateStr, status, holidayName: holName, leaveType, segments, weekend });
    d.setDate(d.getDate() + 1);
  }
  return result.reverse();
}

/* ─────────────────────────────────────────────
   COMPUTE STATS
───────────────────────────────────────────── */
function computeStats(days) {
  let office = 0, wfh = 0, travel = 0, leave = 0;
  for (const d of days) {
    if (d.status === "leave") { leave++; continue; }
    if (d.status === "travel") { travel++; continue; }
    if (d.status === "weekend" || d.status === "holiday") continue;
    if (d.segments.length === 0) continue;
    if (d.segments.some((s) => s.source === "office")) office++;
    else wfh++;
  }
  return { office, wfh, travel, leave };
}

/* ─────────────────────────────────────────────
   STATUS STYLE MAP
───────────────────────────────────────────── */
const STATUS_STYLE = {
  office:  { bg: "#f0fdf4", border: "#bbf7d0", dot: "#16a34a", text: "#15803d", label: "Office",   iconBg: "#dcfce7", iconColor: "#16a34a" },
  wfh:     { bg: "#eff6ff", border: "#bfdbfe", dot: "#2563eb", text: "#1d4ed8", label: "WFH",      iconBg: "#dbeafe", iconColor: "#2563eb" },
  travel:  { bg: "#fff7ed", border: "#fed7aa", dot: "#ea580c", text: "#c2410c", label: "Travel",   iconBg: "#fed7aa", iconColor: "#ea580c" },
  leave:   { bg: "#fdf2f8", border: "#fbcfe8", dot: "#db2777", text: "#be185d", label: "Leave",    iconBg: "#fce7f3", iconColor: "#db2777" },
  holiday: { bg: "#fefce8", border: "#fde68a", dot: "#ca8a04", text: "#a16207", label: "Holiday",  iconBg: "#fef9c3", iconColor: "#ca8a04" },
  weekend: { bg: "#f9fafb", border: "#e5e7eb", dot: "#9ca3af", text: "#6b7280", label: "Weekend",  iconBg: "#f3f4f6", iconColor: "#9ca3af" },
  workday: { bg: "#f9fafb", border: "#e5e7eb", dot: "#9ca3af", text: "#6b7280", label: "No Login", iconBg: "#f3f4f6", iconColor: "#9ca3af" },
};

/* ─────────────────────────────────────────────
   ISSUE BADGE
───────────────────────────────────────────── */
const ISSUE_LABEL = {
  no_login:     "No login",
  late_login:   "Late login",
  no_logout:    "No logout",
  early_logout: "Left early",
};

function IssueBadge({ issueType, onClick, alreadySaved }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={`${ISSUE_LABEL[issueType] ?? "Issue"} — click to explain`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 8px", borderRadius: 999, cursor: "pointer",
        fontSize: 11, fontWeight: 700,
        border: `1.5px solid ${alreadySaved ? "#bbf7d0" : "#fca5a5"}`,
        background: alreadySaved ? "#f0fdf4" : "#fff1f2",
        color: alreadySaved ? "#15803d" : "#dc2626",
        transition: "all 0.15s", flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3" />
        <line x1="12" y1="12" x2="12" y2="16" />
      </svg>
      {alreadySaved ? "Explained" : (ISSUE_LABEL[issueType] ?? "Issue")}
    </button>
  );
}

/* ─────────────────────────────────────────────
   SESSION ROW
───────────────────────────────────────────── */
function SessionRow({ session }) {
  const isOffice = session.source === "office";
  const s = isOffice ? STATUS_STYLE.office : STATUS_STYLE.wfh;
  const label = isOffice ? "Logged in from office" : "Working from home";
  const loginFmt = formatTime(session.firstLogin);
  const logoutFmt = session.lastLogout ? formatTime(session.lastLogout) : null;

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "10px 12px", borderRadius: 10,
      background: s.bg, border: `1px solid ${s.border}`,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: s.iconBg, color: s.iconColor,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {isOffice ? <IconBriefcase size={17} /> : <IconHome size={17} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{
          fontSize: 12, color: "#6b7280",
          display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap",
        }}>
          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 550 }}>{loginFmt}</span>
          {logoutFmt ? (
            <>
              <IconArrow />
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 550 }}>{logoutFmt}</span>
            </>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#dc2626",
              background: "#fff1f2", border: "1px solid #fca5a5",
              borderRadius: 4, padding: "1px 5px",
            }}>
              no logout
            </span>
          )}
          <span style={{ color: "#d1d5db" }}>·</span>
          <span>{session.count} {session.count === 1 ? "login" : "logins"}</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DAY CARD
───────────────────────────────────────────── */
function DayCard({ day, onIssueClick, savedDates }) {
  const { date, status, holidayName, leaveType, segments } = day;

  let displayStatus = status;
  if (status === "workday" && segments.length > 0) {
    displayStatus = segments.some((s) => s.source === "office") ? "office" : "wfh";
  }

  const style = STATUS_STYLE[displayStatus] || STATUS_STYLE.workday;
  const isNonWorking = status === "weekend" || status === "holiday";

  let badgeText = style.label;
  if (status === "holiday" && holidayName) badgeText = holidayName;
  if (status === "leave" && leaveType) badgeText = leaveType;

  const sessions = segments.map(s => ({
    source: s.source,
    firstLogin: s.loginTime || s.time,
    lastLogout: s.logoutTime,
    count: 1,
}));
  const issues = detectIssues(day);
  const totalLogins = segments.length;

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${
        isNonWorking ? "#f3f4f6"
          : issues.length && !savedDates.has(date) ? "#fecaca"
          : style.border
      }`,
      borderRadius: 14,
      padding: "14px 16px",
      opacity: isNonWorking ? 0.5 : 1,
      boxShadow: isNonWorking ? "none"
        : issues.length && !savedDates.has(date)
          ? "0 1px 4px rgba(220,38,38,0.10)"
          : "0 1px 3px rgba(0,0,0,0.05)",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: 10, gap: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#9ca3af",
            textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {new Date(date + "T00:00:00").toLocaleDateString("en-US", {
              weekday: "long", month: "long", day: "numeric",
            })}
          </div>
          <span style={{
            fontSize: 11, fontWeight: 650, padding: "3px 9px", borderRadius: 999,
            background: style.bg, color: style.text, border: `1px solid ${style.border}`,
            display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: style.dot, flexShrink: 0 }} />
            {badgeText}
          </span>
        </div>

        <div style={{
          display: "flex", flexDirection: "column",
          alignItems: "flex-end", gap: 4, flexShrink: 0,
        }}>
          {totalLogins > 0 && (
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: 22, fontWeight: 750, color: "#111827", lineHeight: 1 }}>
                {totalLogins}
              </span>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4 }}>logins</span>
            </div>
          )}
          {issues.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
              {issues.map((issue) => (
                <IssueBadge
                  key={issue}
                  issueType={issue}
                  onClick={() => onIssueClick(day, issue)}
                  alreadySaved={savedDates.has(date)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sessions */}
      {sessions.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {sessions.map((session, i) => (
            <SessionRow key={i} session={session} />
          ))}
        </div>
      ) : !isNonWorking ? (
        <p style={{ fontSize: 12, color: "#9ca3af", margin: 0, fontStyle: "italic" }}>
          No login recorded
        </p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────
   STAT TILE
───────────────────────────────────────────── */
const TILE_TONE = {
  office: { iconBg: "#dcfce7", iconColor: "#16a34a", ring: "#16a34a22", ringBorder: "#16a34a" },
  wfh:    { iconBg: "#dbeafe", iconColor: "#2563eb", ring: "#2563eb22", ringBorder: "#2563eb" },
  travel: { iconBg: "#fed7aa", iconColor: "#ea580c", ring: "#ea580c22", ringBorder: "#ea580c" },
  leave:  { iconBg: "#fce7f3", iconColor: "#db2777", ring: "#db277722", ringBorder: "#db2777" },
};

function StatTile({ label, value, tone, Icon, active, onClick, compact }) {
  const t = TILE_TONE[tone];
  return (
    <button type="button" onClick={onClick} style={{
      background: "#fff",
      border: active ? `2px solid ${t.ringBorder}` : "1px solid #e5e7eb",
      borderRadius: 14,
      padding: compact ? "12px 14px" : "16px 18px",
      display: "flex", alignItems: "center",
      gap: compact ? 10 : 14,
      cursor: "pointer", textAlign: "left",
      boxShadow: active ? `0 0 0 4px ${t.ring}` : "0 1px 2px rgba(0,0,0,0.04)",
      transition: "all 0.15s", width: "100%",
    }}>
      <div style={{
        width: compact ? 36 : 42,
        height: compact ? 36 : 42,
        borderRadius: 10, flexShrink: 0,
        background: t.iconBg, color: t.iconColor,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={compact ? 17 : 20} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: compact ? 24 : 28,
          fontWeight: 750, color: "#111827", lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}>
          {value}
        </div>
        <div style={{
          fontSize: 11, color: "#6b7280", marginTop: 4, lineHeight: 1.3,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
          {active && (
            <span style={{ marginLeft: 5, color: "#111827", fontWeight: 700 }}>· filtered</span>
          )}
        </div>
      </div>
    </button>
  );
}

/* ─────────────────────────────────────────────
   PERIOD TAB
───────────────────────────────────────────── */
function PeriodTab({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: "7px 14px", borderRadius: 8, border: "none",
      background: active ? "#fff" : "transparent",
      color: active ? "#111827" : "#6b7280",
      fontSize: 13, fontWeight: active ? 650 : 500,
      cursor: "pointer",
      boxShadow: active ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
      transition: "all 0.15s",
      whiteSpace: "nowrap",
    }}>
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const UserAttendanceView = ({ viewUserId: propViewUserId = null }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();
  // const viewUserId = location.state?.viewUserId ?? null;
  const viewUserId = propViewUserId ?? location.state?.viewUserId ?? null;
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [period, setPeriod] = useState("week");
  const [filter, setFilter] = useState(null);
  const [empRecord, setEmpRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalDay, setModalDay] = useState(null);
  const [modalIssue, setModalIssue] = useState(null);
  const [savedDates, setSavedDates] = useState(new Set());

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = viewUserId
        ? `${API_BASE_URL}/employeeinfos/personalinfos/attendanceview?userId=${viewUserId}`
        : `${API_BASE_URL}/employeeinfos/personalinfos/attendanceview`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setEmpRecord(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
      setError("Failed to load attendance data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [viewUserId, API_BASE_URL, token]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const allDays = useMemo(() => {
    if (!empRecord) return [];
    return buildDaysForPeriod(empRecord, period);
  }, [empRecord, period]);

  const stats = useMemo(() => computeStats(allDays), [allDays]);

  const filteredDays = useMemo(() => {
    if (!filter) return allDays;
    if (filter === "office") return allDays.filter((d) => d.segments.some((s) => s.source === "office"));
    if (filter === "wfh")    return allDays.filter((d) => d.segments.some((s) => s.source === "wfh") && !d.segments.some((s) => s.source === "office"));
    if (filter === "travel") return allDays.filter((d) => d.status === "travel");
    if (filter === "leave")  return allDays.filter((d) => d.status === "leave");
    return allDays;
  }, [allDays, filter]);

  const handleIssueClick = (day, issueType) => { setModalDay(day); setModalIssue(issueType); };
  const handleIssueSaved = (date) => setSavedDates((prev) => new Set([...prev, date]));
  const handleModalClose = () => { setModalDay(null); setModalIssue(null); };

  const headingPeriodText =
    period === "week"  ? "last 7 days"  :
    period === "month" ? "last 30 days" :
    "last 365 days";

  /* ── Responsive values ── */
  const px = isMobile ? 16 : 28;

  const statGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "repeat(2, 1fr)"
      : isTablet
      ? "repeat(2, 1fr)"
      : "repeat(4, 1fr)",
    gap: isMobile ? 10 : 12,
    marginBottom: 28,
  };

  const dayGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : isTablet
      ? "1fr"
      : "repeat(auto-fill, minmax(360px, 1fr))",
    gap: 12,
  };

  const heroFontSize = isMobile ? 26 : isTablet ? 30 : 34;

  return (
    <div style={{
      minHeight: "100vh", background: "#f9fafb",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>

      {/* ── Global responsive styles ── */}
      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 639px) {
          .period-tabs-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .period-tabs-scroll::-webkit-scrollbar { display: none; }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={{
        background: "#fff", borderBottom: "1px solid #e5e7eb",
        padding: `0 ${px}px`, height: isMobile ? 52 : 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
        gap: 12,
      }}>
        <span style={{
          fontWeight: 750, fontSize: isMobile ? 15 : 17,
          color: "#111827", letterSpacing: "-0.02em", flexShrink: 0,
        }}>
          Work<span style={{ color: "#16a34a" }}>Track</span>
        </span>
        {empRecord && (
          <span style={{
            fontSize: isMobile ? 12 : 13,
            color: "#6b7280", fontWeight: 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            minWidth: 0,
          }}>
            {empRecord.fullName}
            {empRecord.worklocationName ? ` · ${empRecord.worklocationName}` : ""}
          </span>
        )}
      </header>

      <main style={{ padding: `${isMobile ? 20 : 28}px ${px}px 0` }}>

        {/* ── Hero ── */}
        <section style={{ marginBottom: 20 }}>
          <h2 style={{
            fontSize: heroFontSize, fontWeight: 750, color: "#111827",
            letterSpacing: "-0.02em", margin: 0, lineHeight: 1.2,
          }}>
            Your {headingPeriodText},{" "}
            <span style={{
              background: "linear-gradient(135deg, #16a34a 0%, #2563eb 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              decoded
            </span>.
          </h2>
          {!isMobile && (
            <p style={{
              marginTop: 8, color: "#6b7280", fontSize: 14,
              maxWidth: 560, lineHeight: 1.6,
            }}>
              Every login is tagged as office, travel or work-from-home.
              Leave, holidays and weekends override activity.
            </p>
          )}
        </section>

        {/* ── Period tabs ── */}
        <div
          className="period-tabs-scroll"
          style={{ marginBottom: 20 }}
        >
          <div style={{
            display: "inline-flex", gap: 2,
            background: "#f3f4f6", borderRadius: 10, padding: "3px",
            minWidth: "max-content",
          }}>
            {[
              { value: "week",  label: "This Week"  },
              { value: "month", label: "This Month" },
              { value: "year",  label: "FY 2026–27" },
            ].map((t) => (
              <PeriodTab
                key={t.value} label={t.label} active={period === t.value}
                onClick={() => { setPeriod(t.value); setFilter(null); }}
              />
            ))}
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 80, color: "#6b7280" }}>
            <div style={{
              width: 32, height: 32,
              border: "3px solid #e5e7eb", borderTopColor: "#16a34a",
              borderRadius: "50%", animation: "spin 0.7s linear infinite",
              margin: "0 auto 12px",
            }} />
            Loading your attendance…
          </div>
        )}

        {/* ── Error ── */}
        {!loading && error && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <p style={{ color: "#ef4444", marginBottom: 12 }}>{error}</p>
            <button
              onClick={fetchAttendance}
              style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: "#111827", color: "#fff", fontWeight: 600, cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Content ── */}
        {!loading && !error && empRecord && (
          <>
            {/* Stat tiles */}
            <section style={statGridStyle}>
              <StatTile
                label={`Office · ${PERIOD_LABEL[period]}`}
                value={stats.office} tone="office"
                Icon={IconBriefcase} compact={isMobile || isTablet}
                active={filter === "office"}
                onClick={() => setFilter(filter === "office" ? null : "office")}
              />
              <StatTile
                label={`WFH · ${PERIOD_LABEL[period]}`}
                value={stats.wfh} tone="wfh"
                Icon={IconHome} compact={isMobile || isTablet}
                active={filter === "wfh"}
                onClick={() => setFilter(filter === "wfh" ? null : "wfh")}
              />
              <StatTile
                label={`Travel · ${PERIOD_LABEL[period]}`}
                value={stats.travel} tone="travel"
                Icon={IconPlane} compact={isMobile || isTablet}
                active={filter === "travel"}
                onClick={() => setFilter(filter === "travel" ? null : "travel")}
              />
              <StatTile
                label={`Leave · ${PERIOD_LABEL[period]}`}
                value={stats.leave} tone="leave"
                Icon={IconPalmtree} compact={isMobile || isTablet}
                active={filter === "leave"}
                onClick={() => setFilter(filter === "leave" ? null : "leave")}
              />
            </section>

            {/* Timeline */}
            <section>
              <div style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between", marginBottom: 12, gap: 8,
                flexWrap: "wrap",
              }}>
                <h3 style={{
                  fontSize: 11, fontWeight: 700, color: "#9ca3af",
                  textTransform: "uppercase", letterSpacing: "0.1em", margin: 0,
                }}>
                  Daily timeline
                  {filter && (
                    <span style={{
                      marginLeft: 8, color: "#111827",
                      textTransform: "none", letterSpacing: "normal", fontWeight: 600,
                    }}>
                      · {FILTER_LABEL[filter]}
                    </span>
                  )}
                </h3>
                {filter && (
                  <button
                    onClick={() => setFilter(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "5px 12px", borderRadius: 999,
                      border: "1px solid #e5e7eb", background: "#fff",
                      fontSize: 12, fontWeight: 550, color: "#6b7280",
                      cursor: "pointer", flexShrink: 0,
                    }}
                  >
                    <IconX /> Clear filter
                  </button>
                )}
              </div>

              {filteredDays.length === 0 ? (
                <div style={{
                  border: "1.5px dashed #e5e7eb", borderRadius: 14,
                  padding: isMobile ? 40 : 60,
                  textAlign: "center", fontSize: 14, color: "#9ca3af",
                }}>
                  No days match this filter in {PERIOD_LABEL[period].toLowerCase()}.
                </div>
              ) : (
                <div style={dayGridStyle}>
                  {filteredDays.map((d) => (
                    <DayCard
                      key={d.date} day={d}
                      onIssueClick={handleIssueClick}
                      savedDates={savedDates}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {!loading && !error && !empRecord && (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
            No attendance data available.
          </div>
        )}

        <footer style={{
          marginTop: 40, paddingBottom: 20,
          textAlign: "center", fontSize: 12, color: "#d1d5db",
        }}>
          WorkTrack · FY 2026–27 · {FY_START} → {FY_END}
        </footer>
      </main>

      {/* Modal */}
      {modalDay && (
        <AttendanceIssueModal
          day={modalDay}
          issueType={modalIssue}
          onClose={handleModalClose}
          onSaved={handleIssueSaved}
        />
      )}
    </div>
  );
};

export default UserAttendanceView;